import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Spinner,
    UncontrolledDropdown
} from 'reactstrap';
import {Building2, Check} from 'lucide-react';
import useGetMyCompanies from '../../api/auth/useGetMyCompanies';
import useSwitchCompany from '../../api/auth/useSwitchCompany';
import {toast} from 'react-toastify';
import {RoutePaths} from '../../RoutePaths';

// Shows the user's current company in the navbar. If the user belongs to more
// than one company, it becomes a dropdown that switches the active company.
// "Company" is the user-facing term for what the backend calls a "customer".
const CompanySwitcher = () => {
    const {
        isAuthenticated,
        hasCustomerLogin,
        hasMultipleCustomers,
        customerId,
        onAuthenticated
    } = useAuthStateManager();

    const enabled = isAuthenticated && hasCustomerLogin;

    const {data: companies} = useGetMyCompanies(enabled);

    const switchCompany = useSwitchCompany({
        onSuccess: tokens => {
            // Persist the newly-issued tokens, then hard-reload so every
            // company-scoped view re-fetches cleanly under the new context.
            onAuthenticated(tokens);
            window.location.assign(RoutePaths.dashboard());
        },
        onError: () => {
            toast.error('Unable to switch company. Please try again.');
        }
    });

    if (!enabled) {
        return null;
    }

    const currentCompany =
        companies?.find(c => c.customerId === customerId) ??
        companies?.find(c => c.isCurrentlySelected);

    const currentName = currentCompany?.name;

    // Single-company users: show the company name as plain navbar text.
    if (!hasMultipleCustomers) {
        return (
            <span className="navbar-text d-flex align-items-center text-white me-3">
                <Building2 size={16} className="me-2" />
                {currentName ?? ''}
            </span>
        );
    }

    return (
        <UncontrolledDropdown nav inNavbar>
            <DropdownToggle nav caret disabled={switchCompany.isPending}>
                <Building2 size={16} className="me-2" />
                {switchCompany.isPending ? (
                    <Spinner size="sm" />
                ) : (
                    (currentName ?? 'Select company')
                )}
            </DropdownToggle>
            <DropdownMenu className="dropdown-left">
                <DropdownItem header>My Companies</DropdownItem>
                {(companies ?? []).map(company => {
                    const isCurrent = company.customerId === customerId;
                    return (
                        <DropdownItem
                            key={company.customerId}
                            active={isCurrent}
                            disabled={isCurrent || switchCompany.isPending}
                            onClick={() =>
                                switchCompany.mutate(company.customerId)
                            }
                        >
                            <Building2 size={14} className="me-2" />
                            {company.name}
                            {isCurrent && (
                                <Check
                                    size={14}
                                    className="ms-2 text-success"
                                />
                            )}
                        </DropdownItem>
                    );
                })}
            </DropdownMenu>
        </UncontrolledDropdown>
    );
};

export default CompanySwitcher;
